import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';
import useCrossWindowSync from '../hooks/useCrossWindowSync';
import './GlobalNotificationCenter.css';

function GlobalNotificationCenter() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, critical: 0, high: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 🔄 Auto-refresh pour les notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [notifResponse, statsResponse] = await Promise.all([
        axios.get('http://localhost:5000/api/global-notifications'),
        axios.get('http://localhost:5000/api/global-notifications/stats')
      ]);
      
      setNotifications(notifResponse.data);
      setStats(statsResponse.data);
      setError('');
      return notifResponse.data;
    } catch (err) {
      console.error('Erreur récupération notifications globales:', err);
      setError('Erreur lors du chargement des notifications');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const { isRefreshing, forceRefresh } = useAutoRefresh(
    fetchNotifications,
    [],
    {
      interval: 5000, // 5 secondes pour les notifications critiques
      enabled: true,
      collaborationMode: true
    }
  );

  // 🌐 Synchronisation inter-fenêtres
  const { triggerSync } = useCrossWindowSync('global_notifications_updated', useCallback(() => {
    forceRefresh();
  }, [forceRefresh]));

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`http://localhost:5000/api/global-notifications/${notificationId}/read`);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: 1, is_new: false }
            : notif
        )
      );
      
      triggerSync({ action: 'mark_read', notificationId, userId: user.id });
    } catch (err) {
      console.error('Erreur marquage notification:', err);
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await axios.put(`http://localhost:5000/api/global-notifications/${notificationId}/dismiss`);
      
      setNotifications(prev => 
        prev.filter(notif => notif.id !== notificationId)
      );
      
      triggerSync({ action: 'dismiss', notificationId, userId: user.id });
    } catch (err) {
      console.error('Erreur masquage notification:', err);
    }
  };

  const createTestNotification = async (type = 'alert') => {
    try {
      const testNotification = {
        type: type,
        title: type === 'critical' ? '🚨 ARRÊT D\'URGENCE LIGNE A' : '⚠️ Test notification globale',
        message: type === 'critical' 
          ? 'Arrêt immédiat requis - Problème de sécurité détecté sur la ligne A. Contactez immédiatement l\'équipe de maintenance.'
          : `Test de notification globale créé par ${user.prenom} ${user.nom} à ${new Date().toLocaleTimeString()}`,
        priority: type === 'critical' ? 'critical' : 'normal',
        target_department: user.departement || null, // Notification pour le département de l'utilisateur
        source_resource: 'formulaire'
      };

      await axios.post('http://localhost:5000/api/global-notifications', testNotification);
      
      triggerSync({ action: 'new_notification', userId: user.id, type: type });
      forceRefresh();
    } catch (err) {
      console.error('Erreur création notification test:', err);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'normal': return '📢';
      case 'low': return 'ℹ️';
      default: return '📢';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'comment': return '💬';
      case 'mention': return '👤';
      case 'alert': return '⚠️';
      case 'incident': return '🚨';
      case 'maintenance': return '🔧';
      case 'quality': return '✅';
      case 'security': return '🛡️';
      default: return '📢';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'normal': return '#2563eb';
      case 'low': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // Calcul du badge de notification
  const badgeCount = stats.critical > 0 ? stats.critical : stats.unread;
  const badgeColor = stats.critical > 0 ? '#dc2626' : stats.high > 0 ? '#ea580c' : '#2563eb';

  return (
    <>
      {/* 🔔 Badge de notification flottant */}
      <div className={`global-notification-badge ${badgeCount > 0 ? 'has-notifications' : ''}`}>
        <button
          className="notification-button"
          onClick={() => setIsOpen(!isOpen)}
          title={`${badgeCount} notification(s) non lue(s)`}
        >
          🔔
          {badgeCount > 0 && (
            <span 
              className="notification-count" 
              style={{ backgroundColor: badgeColor }}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </button>
      </div>

      {/* 📋 Panel des notifications */}
      {isOpen && (
        <div className="global-notification-panel">
          <div className="notification-header">
            <h3>🔔 Notifications Globales</h3>
            <div className="header-controls">
              {isRefreshing && (
                <span className="refresh-indicator" title="Synchronisation en cours...">🔄</span>
              )}
              <button
                className="btn-refresh"
                onClick={forceRefresh}
                disabled={isRefreshing}
                title="Actualiser les notifications"
              >
                ⟳
              </button>
              <button
                className="btn-close"
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>
            </div>
          </div>

          {/* 📊 Statistiques rapides */}
          <div className="notification-stats">
            <div className="stat-item">
              <span className="stat-label">Total</span>
              <span className="stat-value">{stats.total}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Non lues</span>
              <span className="stat-value unread">{stats.unread}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Critiques</span>
              <span className="stat-value critical">{stats.critical}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Importantes</span>
              <span className="stat-value high">{stats.high}</span>
            </div>
          </div>

          {/* 🧪 Boutons de test */}
          <div className="test-buttons">
            <button 
              className="test-btn normal"
              onClick={() => createTestNotification('alert')}
            >
              📢 Test Normal
            </button>
            <button 
              className="test-btn critical"
              onClick={() => createTestNotification('critical')}
            >
              🚨 Test Critique
            </button>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          {/* 📋 Liste des notifications */}
          <div className="notifications-list">
            {loading && notifications.length === 0 ? (
              <div className="loading">Chargement des notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <p>Aucune notification pour le moment.</p>
                <p>🎉 Tout va bien !</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.priority} ${notification.is_new ? 'new' : ''} ${notification.is_read ? 'read' : 'unread'}`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <div className="notification-content">
                    <div className="notification-header-item">
                      <span className="notification-icons">
                        {getPriorityIcon(notification.priority)}
                        {getTypeIcon(notification.type)}
                      </span>
                      <h4 className="notification-title">{notification.title}</h4>
                      <span className="notification-time">{notification.time_ago}</span>
                    </div>
                    
                    <p className="notification-message">{notification.message}</p>
                    
                    <div className="notification-meta">
                      <span className="notification-source">
                        De: {notification.source_user_name} ({notification.source_user_role})
                      </span>
                      {notification.source_formulaire_id && (
                        <span className="notification-form">
                          Formulaire: {notification.source_formulaire_id.substring(0, 12)}...
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="notification-actions">
                    <button
                      className="btn-dismiss"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissNotification(notification.id);
                      }}
                      title="Masquer cette notification"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default GlobalNotificationCenter;
